import { ProForm, ProFormDatePicker } from '@ant-design/pro-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Card, Spin } from 'antd';
import PropTypes from 'prop-types';
import {
  queryMetricEffectivePeriod,
  saveMetricEffectivePeriod,
} from '@/common/services/metric-effective-period';
import {
  OPERATION_ANALYSIS_METADATA_STALE_TIME,
  getMetricEffectivePeriodQueryKey,
  getMetricEffectiveStartDate,
  toMetricEffectiveStartAt,
} from '@/common/utils/metric-effective-period';

const MetricEffectivePeriodConfig = ({ organizationId }) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const queryKey = getMetricEffectivePeriodQueryKey(organizationId);
  const {
    data: response = {},
    isError,
    isPending,
  } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryMetricEffectivePeriod({ organizationId }),
    queryKey,
    staleTime: OPERATION_ANALYSIS_METADATA_STALE_TIME,
  });
  const { isPending: isSaving, mutateAsync: savePeriod } = useMutation({
    mutationFn: saveMetricEffectivePeriod,
  });
  const effectiveStartAt = response.data?.effectiveStartAt ?? null;

  const save = async ({ effectiveStartDate }) => {
    try {
      await savePeriod({
        data: {
          effectiveStartAt: toMetricEffectiveStartAt(effectiveStartDate),
          organizationId,
        },
      });
      await queryClient.invalidateQueries({ queryKey });
      message.success('指标有效周期已保存');
      return true;
    } catch {
      return false;
    }
  };

  return (
    <Card title="指标有效周期" variant="borderless">
      <Spin spinning={isPending}>
        {isError ? (
          <Alert message="指标有效周期加载失败，请刷新重试" showIcon type="error" />
        ) : (
          <ProForm
            key={String(effectiveStartAt)}
            initialValues={{
              effectiveStartDate: getMetricEffectiveStartDate(effectiveStartAt) || undefined,
            }}
            submitter={{
              searchConfig: { submitText: '保存' },
              submitButtonProps: { loading: isSaving },
              resetButtonProps: { style: { display: 'none' } },
            }}
            onFinish={save}
          >
            <ProFormDatePicker
              allowClear={false}
              label="指标有效起始日期"
              name="effectiveStartDate"
              rules={[{ message: '请选择指标有效起始日期', required: true }]}
              width="md"
            />
          </ProForm>
        )}
      </Spin>
    </Card>
  );
};

MetricEffectivePeriodConfig.propTypes = {
  organizationId: PropTypes.string.isRequired,
};

export default MetricEffectivePeriodConfig;
